/**
 * EpochTransitionCurtain — церемония перехода между эпохами.
 *
 * Doc 28 §G cycles audit: переход в новую эпоху случался тихо — `epoch`
 * увеличивался в state, EpochBadge обновлял numeral, но никакого
 * особого момента не было. Это самый важный milestone в игре (1 из 11
 * за всю историю до Эпохи 12). Должен быть момент тишины и торжества.
 *
 * Принцип #12 «Бесшовный флоу»: занавес — медленный fade-in/out, без
 * hard cut. Не блокирует игру (pointer-events: none) — игрок может
 * продолжать тапать, занавес просто покрывает экран мягким золотом
 * и сообщением о новой эпохе + её тональности.
 *
 * Тайминг:
 *   - fade-in: 800ms
 *   - hold: 2200ms (читается название + key)
 *   - fade-out: 1200ms
 *   - Total: 4.2s
 *
 * Wire: mount once в App.tsx. Renders null когда нет перехода.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useGameStore } from '../stores/game.js';
import styles from './EpochTransitionCurtain.module.css';

const EPOCH_NAMES = [
  'искра',
  'фрактальный метод',
  'ФРАКТАЛiТи',
  'МОГА',
  'Sai Land Heal',
  'МОГАзинус',
  'МОГАsound',
  'ретриты',
  'сеть Рощ',
  'светогромовое шоу',
  'новый мир',
  'создатель',
] as const;

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'] as const;

/** Doc 20 — тональность по эпохе (cycle of fifths, descending). */
const EPOCH_KEYS = [
  'A minor',
  'E minor',
  'B minor',
  'F♯ minor',
  'C♯ minor',
  'G♯ minor',
  'D♯ minor',
  'A♯ minor',
  'F minor',
  'C minor',
  'G minor',
  'D minor',
] as const;

interface Transition {
  readonly from: number;
  readonly to: number;
  /** Key для React — каждый переход уникален. */
  readonly firedAt: number;
}

export function EpochTransitionCurtain(): JSX.Element {
  const epoch = useGameStore((s) => s.state.epoch);
  const prevEpochRef = useRef<number>(epoch);
  const [transition, setTransition] = useState<Transition | null>(null);

  useEffect(() => {
    if (epoch === prevEpochRef.current) return undefined;
    if (epoch < prevEpochRef.current) {
      // Эпоха не должна откатываться, но на всякий — без церемонии.
      prevEpochRef.current = epoch;
      return undefined;
    }
    const prev = prevEpochRef.current;
    prevEpochRef.current = epoch;
    setTransition({ from: prev, to: epoch, firedAt: Date.now() });
    const tid = window.setTimeout(() => {
      setTransition(null);
    }, 4200);
    return () => {
      window.clearTimeout(tid);
    };
  }, [epoch]);

  return (
    <AnimatePresence>
      {transition !== null && <CurtainCard key={transition.firedAt} transition={transition} />}
    </AnimatePresence>
  );
}

interface CardProps {
  readonly transition: Transition;
}

function CurtainCard({ transition }: CardProps): JSX.Element {
  const fromName = EPOCH_NAMES[transition.from - 1] ?? '';
  const toName = EPOCH_NAMES[transition.to - 1] ?? '';
  const toNumeral = ROMAN[transition.to - 1] ?? '';
  const toKey = EPOCH_KEYS[transition.to - 1] ?? '';

  return (
    <motion.div
      className={styles.curtain}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        opacity: { duration: 1.2, ease: 'easeInOut' },
      }}
      aria-hidden
    >
      <motion.div
        className={styles.card}
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -8, scale: 0.97 }}
        transition={{ duration: 0.9, ease: [0.25, 0.8, 0.3, 1] }}
      >
        <p className={styles.preface}>переход</p>
        {/* Старая эпоха — мелким шрифтом, проходящая. */}
        <p className={styles.from}>
          <span className={styles.fromLabel}>было · </span>
          {fromName}
        </p>
        {/* Большая стрелка-разделитель. */}
        <div className={styles.divider} aria-hidden>
          ◈
        </div>
        {/* Новая эпоха — крупно, торжественно. */}
        <p className={styles.toNumeral}>эпоха {toNumeral}</p>
        <p className={styles.toName}>{toName}</p>
        <p className={styles.toKey}>тональность · {toKey}</p>
      </motion.div>
    </motion.div>
  );
}
